import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  Users, 
  Briefcase, 
  Shield, 
  Heart, 
  Calculator,
  FileText,
  Clock,
  CheckCircle
} from 'lucide-react';
import { ApplicationAssignment } from '@/types/assignment.types';
import { toast } from 'sonner';

interface QuoteTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  industry: string[];
  employeeRange: string[];
  services: {
    category: string;
    items: {
      name: string;
      description: string;
      basePrice?: number;
      perEmployee?: number;
      included: boolean;
    }[];
  }[];
}

const quoteTemplates: QuoteTemplate[] = [
  {
    id: 'small_business',
    name: 'Small Business Package',
    description: 'Comprehensive PEO services for small businesses (2-50 employees)',
    icon: Building2,
    industry: ['general'],
    employeeRange: ['2-50'],
    services: [
      {
        category: 'HR Administration',
        items: [
          { name: 'HR Support & Compliance', description: 'Basic HR support and compliance management', perEmployee: 45, included: true },
          { name: 'Employee Handbook', description: 'Custom employee handbook creation', basePrice: 500, included: true },
          { name: 'Performance Management', description: 'Annual performance review system', perEmployee: 15, included: false },
        ]
      },
      {
        category: 'Payroll Services',
        items: [
          { name: 'Payroll Processing', description: 'Bi-weekly payroll processing and tax filing', perEmployee: 25, included: true },
          { name: 'Direct Deposit', description: 'Electronic payroll deposits', perEmployee: 2, included: true },
          { name: 'Year-end Tax Services', description: 'W-2s, 1099s, and tax reporting', basePrice: 200, included: true },
        ]
      },
      {
        category: 'Benefits Administration',
        items: [
          { name: 'Health Insurance', description: 'Group health insurance options', perEmployee: 350, included: false },
          { name: 'Dental Insurance', description: 'Group dental coverage', perEmployee: 45, included: false },
          { name: '401(k) Plan', description: 'Retirement plan administration', perEmployee: 35, included: false },
        ]
      },
      {
        category: 'Workers Compensation',
        items: [
          { name: 'Workers Comp Insurance', description: 'Comprehensive workers compensation coverage', perEmployee: 85, included: true },
          { name: 'Safety Program', description: 'Workplace safety training and compliance', basePrice: 750, included: false },
        ]
      }
    ]
  },
  {
    id: 'mid_market',
    name: 'Mid-Market Enterprise',
    description: 'Advanced PEO solutions for growing companies (51-200 employees)',
    icon: Users,
    industry: ['technology', 'manufacturing', 'professional_services'],
    employeeRange: ['51-200'],
    services: [
      {
        category: 'HR Administration',
        items: [
          { name: 'Dedicated HR Manager', description: 'Dedicated HR business partner', basePrice: 2500, included: true },
          { name: 'Advanced Compliance', description: 'Multi-state compliance management', perEmployee: 55, included: true },
          { name: 'Talent Acquisition', description: 'Recruitment and hiring support', perEmployee: 125, included: false },
        ]
      },
      {
        category: 'Payroll & Tax',
        items: [
          { name: 'Advanced Payroll', description: 'Multi-state payroll with custom reporting', perEmployee: 35, included: true },
          { name: 'Tax Advisory', description: 'Quarterly tax planning and advisory', basePrice: 1200, included: false },
        ]
      },
      {
        category: 'Benefits & Insurance',
        items: [
          { name: 'Enterprise Benefits', description: 'Comprehensive benefits package', perEmployee: 425, included: true },
          { name: 'Executive Benefits', description: 'C-level executive benefit plans', basePrice: 5000, included: false },
        ]
      }
    ]
  },
  {
    id: 'healthcare',
    name: 'Healthcare Specialist',
    description: 'Specialized PEO services for healthcare providers',
    icon: Heart,
    industry: ['healthcare'],
    employeeRange: ['10-500'],
    services: [
      {
        category: 'Healthcare Compliance',
        items: [
          { name: 'HIPAA Compliance', description: 'Complete HIPAA compliance program', perEmployee: 75, included: true },
          { name: 'Medical Credentialing', description: 'Provider credentialing support', perEmployee: 150, included: false },
          { name: 'Quality Assurance', description: 'Healthcare quality programs', basePrice: 2000, included: false },
        ]
      },
      {
        category: 'Specialized HR',
        items: [
          { name: 'Healthcare HR', description: 'Industry-specific HR support', perEmployee: 85, included: true },
          { name: 'Continuing Education', description: 'CE tracking and compliance', perEmployee: 25, included: true },
        ]
      }
    ]
  },
  {
    id: 'construction',
    name: 'Construction & Trades',
    description: 'PEO services tailored for construction and trade companies',
    icon: Shield,
    industry: ['construction', 'manufacturing'],
    employeeRange: ['5-300'],
    services: [
      {
        category: 'Safety & Compliance',
        items: [
          { name: 'OSHA Compliance', description: 'Complete OSHA safety program', perEmployee: 95, included: true },
          { name: 'Safety Training', description: 'Regular safety training programs', perEmployee: 45, included: true },
          { name: 'Incident Management', description: '24/7 incident response and reporting', basePrice: 1500, included: true },
        ]
      },
      {
        category: 'High-Risk Insurance',
        items: [
          { name: 'Enhanced Workers Comp', description: 'High-risk workers compensation', perEmployee: 185, included: true },
          { name: 'General Liability', description: 'Comprehensive liability coverage', perEmployee: 65, included: false },
        ]
      }
    ]
  }
];

interface QuoteTemplateSelectorProps {
  assignment: ApplicationAssignment;
  onQuoteCreated: () => void;
}

export const QuoteTemplateSelector = ({ assignment, onQuoteCreated }: QuoteTemplateSelectorProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<QuoteTemplate | null>(null);
  const [customizations, setCustomizations] = useState<{[key: string]: { included: boolean; customPrice?: number }}>({});
  const [quoteNotes, setQuoteNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const employeeCount = assignment.application?.w2_employee_count || 0;
  const industry = assignment.application?.primary_industry?.toLowerCase() || '';

  // Filter templates based on company profile
  const recommendedTemplates = quoteTemplates.filter(template => {
    const employeeMatch = template.employeeRange.some(range => {
      const [min, max] = range.split('-').map(n => parseInt(n) || 999);
      return employeeCount >= min && (max === 999 || employeeCount <= max);
    });
    
    const industryMatch = template.industry.includes('general') || 
                         template.industry.some(ind => industry.includes(ind));
    
    return employeeMatch || industryMatch;
  });

  const calculateTotalCost = (template: QuoteTemplate) => {
    let total = 0;
    
    template.services.forEach(category => {
      category.items.forEach(item => {
        const customization = customizations[`${category.category}-${item.name}`];
        const isIncluded = customization?.included ?? item.included;
        
        if (isIncluded) {
          if (customization?.customPrice) {
            total += customization.customPrice;
          } else {
            if (item.basePrice) total += item.basePrice;
            if (item.perEmployee) total += item.perEmployee * employeeCount;
          }
        }
      });
    });
    
    return total;
  };

  const handleServiceToggle = (categoryName: string, itemName: string, included: boolean) => {
    const key = `${categoryName}-${itemName}`;
    setCustomizations(prev => ({
      ...prev,
      [key]: { ...prev[key], included }
    }));
  };

  const handleCreateQuote = async () => {
    if (!selectedTemplate) return;
    
    setIsCreating(true);
    
    // Simulate quote creation - in real app, this would call the backend
    try {
      // Here you would normally make an API call to create the quote
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success('Quote created successfully!');
      onQuoteCreated();
    } catch (error) {
      toast.error('Failed to create quote. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {!selectedTemplate ? (
        <>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Recommended Templates</h4>
              <p className="text-sm text-muted-foreground">
                Based on {employeeCount} employees in {assignment.application?.primary_industry}
              </p>
            </div>
            
            <div className="grid gap-3">
              {recommendedTemplates.map(template => (
                <Card 
                  key={template.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/50"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <template.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <CardDescription className="text-sm">{template.description}</CardDescription>
                      </div>
                      <Badge variant="secondary">
                        ${Math.round(calculateTotalCost(template) / employeeCount)}/employee/month
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
            
            {recommendedTemplates.length === 0 && (
              <Card className="border-dashed border-2">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
                  <h4 className="font-medium mb-2">No recommended templates</h4>
                  <p className="text-sm text-muted-foreground text-center">
                    Consider using the custom quote builder or contact support for specialized industries.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
          
          <Separator />
          
          <div>
            <h4 className="font-semibold mb-3">All Templates</h4>
            <div className="grid gap-2">
              {quoteTemplates.filter(t => !recommendedTemplates.includes(t)).map(template => (
                <div 
                  key={template.id}
                  className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-center gap-3">
                    <template.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{template.name}</span>
                  </div>
                  <Badge variant="outline">
                    ${Math.round(calculateTotalCost(template) / employeeCount)}/emp/mo
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <selectedTemplate.icon className="h-6 w-6 text-primary" />
              <div>
                <h4 className="font-semibold">{selectedTemplate.name}</h4>
                <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
              Change Template
            </Button>
          </div>
          
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Quote Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Monthly Cost</div>
                    <div className="text-2xl font-bold">${calculateTotalCost(selectedTemplate).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Per Employee</div>
                    <div className="text-2xl font-bold">${Math.round(calculateTotalCost(selectedTemplate) / employeeCount)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {selectedTemplate.services.map(category => (
              <Card key={category.category}>
                <CardHeader>
                  <CardTitle className="text-base">{category.category}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {category.items.map(item => {
                    const key = `${category.category}-${item.name}`;
                    const customization = customizations[key];
                    const isIncluded = customization?.included ?? item.included;
                    
                    return (
                      <div key={item.name} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          onChange={(e) => handleServiceToggle(category.category, item.name, e.target.checked)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.name}</span>
                            <div className="text-sm text-muted-foreground">
                              {item.basePrice && `$${item.basePrice} base`}
                              {item.perEmployee && `$${item.perEmployee}/employee`}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">{item.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quote Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="validUntil">Valid Until</Label>
                  <Input
                    id="validUntil"
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes">Quote Notes</Label>
                  <Textarea
                    id="notes"
                    value={quoteNotes}
                    onChange={(e) => setQuoteNotes(e.target.value)}
                    placeholder="Additional notes or terms for this quote..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
              Back to Templates
            </Button>
            <Button onClick={handleCreateQuote} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Creating Quote...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Create Quote
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};